import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import PageHeader from '../../components/PageHeader.jsx';
import Empty from '../../components/Empty.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { sitesApi } from '../../api/sites.js';
import { analyticsApi } from '../../api/analytics.js';
import { apiError } from '../../api/client.js';

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

// Marketing dashboard. v1.0 surface: pick a site, see traffic over a range,
// inspect top pages / referrers / devices. Future: form submissions, CRM
// integrations, A/B test results.
export default function MarketingPage() {
  const { workspace } = useAuth();
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workspace) return;
    sitesApi
      .list(workspace.id)
      .then((list) => {
        setSites(list);
        const firstPublished = list.find((s) => s.is_published) || list[0];
        if (firstPublished) setSiteId(firstPublished.id);
      })
      .catch((err) => toast.error(apiError(err)));
  }, [workspace?.id]);

  useEffect(() => {
    if (!siteId) {
      setData(null);
      return;
    }
    setLoading(true);
    analyticsApi
      .forSite(siteId, days)
      .then(setData)
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setLoading(false));
  }, [siteId, days]);

  const site = sites.find((s) => s.id === siteId);

  return (
    <>
      <PageHeader
        title="Marketing & SEO"
        description="Per-page SEO is edited inside each site's editor. Traffic, top pages, and referrers for the published site live here."
        actions={
          <div className="flex items-center gap-2">
            <select
              value={siteId}
              onChange={(e) => setSiteId(Number(e.target.value))}
              className="input h-9 w-44 text-sm"
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.is_published ? '' : '· draft'}
                </option>
              ))}
            </select>
            <RangeToggle days={days} onChange={setDays} />
          </div>
        }
      />

      <div className="space-y-6 px-8 py-8">
        {!sites.length ? (
          <Empty title="No sites yet" description="Create a site first to start collecting traffic." />
        ) : !site?.is_published ? (
          <UnpublishedNotice site={site} />
        ) : loading ? (
          <div className="text-sm text-ink-500">Loading analytics…</div>
        ) : data ? (
          <Dashboard site={site} data={data} days={days} />
        ) : null}
      </div>
    </>
  );
}

function RangeToggle({ days, onChange }) {
  return (
    <div className="flex items-center gap-1 rounded-lg border border-ink-200 p-0.5 dark:border-ink-800">
      {RANGES.map((r) => (
        <button
          key={r.days}
          onClick={() => onChange(r.days)}
          className={clsx(
            'h-8 rounded-md px-3 text-xs font-semibold transition',
            days === r.days
              ? 'bg-brand-500 text-white'
              : 'text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800',
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function UnpublishedNotice({ site }) {
  return (
    <div className="card max-w-2xl p-5">
      <h3 className="text-base font-semibold">{site?.name} isn't published yet</h3>
      <p className="mt-1 text-sm text-ink-500">
        Publish a page in the editor to start tracking. Pubvora's tracker is
        privacy-first: no cookies, no third-party scripts, no IPs stored —
        sessions are identified by a daily-salted hash.
      </p>
    </div>
  );
}

// ── dashboard ────────────────────────────────────────────────────────────

function Dashboard({ site, data, days }) {
  const totalViews = data.totals?.views ?? 0;
  const totalSessions = data.totals?.sessions ?? 0;

  return (
    <>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="Pageviews" value={totalViews} />
        <Stat label="Unique sessions" value={totalSessions} />
        <Stat
          label="Views / session"
          value={
            totalSessions
              ? (totalViews / totalSessions).toFixed(1)
              : '—'
          }
        />
        <Stat
          label="Avg / day"
          value={Math.round(totalViews / days)}
        />
      </div>

      <section className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Daily traffic</h2>
          <span className="text-[11px] text-ink-500">last {days} days · UTC</span>
        </div>
        <DailyChart daily={data.daily || []} />
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Table
          title="Top pages"
          rows={(data.top_pages || []).map((p) => ({ key: p.path, value: p.views }))}
          empty="No traffic yet."
          formatKey={(k) => formatPath(site, k)}
        />
        <Table
          title="Top referrers"
          rows={(data.top_referrers || []).map((r) => ({ key: r.referrer_host, value: r.views }))}
          empty="All visits direct so far."
        />
      </div>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-semibold">Devices</h2>
        <DeviceBars devices={data.top_devices || []} />
      </section>

      <section className="card p-5">
        <h2 className="mb-1 text-sm font-semibold">Discovery surfaces</h2>
        <p className="mb-3 text-xs text-ink-500">
          These are auto-generated from your published pages.
        </p>
        <ul className="space-y-1 text-xs">
          <li>
            <span className="text-ink-500">Sitemap:</span>{' '}
            <a
              className="font-mono text-brand-700 hover:underline"
              href={`${publicBase()}/s/${site.slug}/sitemap.xml`}
              target="_blank"
              rel="noreferrer"
            >
              /s/{site.slug}/sitemap.xml
            </a>
          </li>
          <li>
            <span className="text-ink-500">Robots:</span>{' '}
            <a
              className="font-mono text-brand-700 hover:underline"
              href={`${publicBase()}/s/${site.slug}/robots.txt`}
              target="_blank"
              rel="noreferrer"
            >
              /s/{site.slug}/robots.txt
            </a>
          </li>
        </ul>
      </section>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="card p-4">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

// SVG bar chart — height-normalized to the max value in the range, with a
// hover-tooltip showing exact numbers.
function DailyChart({ daily }) {
  const max = useMemo(() => Math.max(1, ...daily.map((d) => d.views)), [daily]);
  const [hover, setHover] = useState(null);

  if (!daily.length) {
    return <div className="py-8 text-center text-xs text-ink-500">No data</div>;
  }

  const W = 100;
  const H = 32;
  const barWidth = W / daily.length;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="block h-32 w-full"
      >
        {daily.map((d, i) => {
          const h = (d.views / max) * (H - 1);
          const x = i * barWidth;
          const y = H - h;
          const active = hover?.i === i;
          return (
            <g key={d.date}>
              <rect
                x={x}
                y={y}
                width={Math.max(barWidth - 0.4, 0.4)}
                height={Math.max(h, 0.3)}
                fill={active ? '#3340c8' : '#5b6cff'}
                opacity={d.views === 0 ? 0.18 : 1}
              />
              {/* Invisible full-height hit area so thin bars are still hoverable. */}
              <rect
                x={x}
                y={0}
                width={barWidth}
                height={H}
                fill="transparent"
                onMouseEnter={() => setHover({ i, ...d })}
                onMouseLeave={() => setHover(null)}
              />
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex items-center justify-between text-[10px] text-ink-400">
        <span>{daily[0]?.date}</span>
        <span>
          {hover
            ? `${hover.date} · ${hover.views} views · ${hover.sessions} sessions`
            : ''}
        </span>
        <span>{daily[daily.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function Table({ title, rows, empty, formatKey }) {
  const total = rows.reduce((acc, r) => acc + (r.value || 0), 0);
  return (
    <section className="card p-5">
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      {rows.length === 0 ? (
        <div className="py-6 text-center text-xs text-ink-500">{empty}</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => {
            const pct = total ? (r.value / total) * 100 : 0;
            return (
              <li key={r.key || '—'} className="relative">
                <div
                  className="absolute inset-0 rounded-md bg-brand-50 dark:bg-ink-800"
                  style={{ width: `${pct}%` }}
                />
                <div className="relative flex items-center justify-between gap-3 px-2 py-1 text-xs">
                  <span className="truncate font-mono">
                    {formatKey ? formatKey(r.key) : (r.key || 'direct / unknown')}
                  </span>
                  <span className="shrink-0 tabular-nums text-ink-700 dark:text-ink-200">
                    {r.value}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function DeviceBars({ devices }) {
  const total = devices.reduce((acc, d) => acc + d.views, 0);
  if (!total) {
    return <div className="py-4 text-center text-xs text-ink-500">No data</div>;
  }
  return (
    <>
      <div className="flex h-3 overflow-hidden rounded-full">
        {devices.map((d) => {
          const pct = (d.views / total) * 100;
          const color = d.device === 'mobile'
            ? '#5b6cff'
            : d.device === 'tablet'
              ? '#7281ff'
              : '#0b0b0e';
          return (
            <div
              key={d.device}
              style={{ width: `${pct}%`, background: color }}
              title={`${d.device}: ${d.views}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
        {devices.map((d) => (
          <span key={d.device} className="text-ink-500">
            <span className="font-semibold text-ink-700 dark:text-ink-200">
              {d.device}
            </span>{' '}
            · {d.views} ({Math.round((d.views / total) * 100)}%)
          </span>
        ))}
      </div>
    </>
  );
}

function publicBase() {
  return import.meta.env.VITE_API_URL || '';
}

// Convert a stored path back to a friendlier label for the top-pages table.
// Stored values look like "/s/my-site/about". We strip the site prefix so
// the user sees just the page slug.
function formatPath(site, path) {
  if (!path) return '/';
  const prefix = `/s/${site.slug}`;
  if (path === prefix || path === `${prefix}/`) return '/ (home)';
  if (path.startsWith(prefix + '/')) return path.slice(prefix.length);
  return path;
}
